import clsx from 'clsx';
import React, { useState, useRef, useEffect } from 'react';

import { FLOW_KEY_FEEDBACK } from '../../../../../../../../store/dashboard/askAica/constants';
import { GLOBAL_ASK_AICA_COPY } from '../../../../../../../../store/globalAskAica/constants';
import { AgentInput } from '../../../../../../../../store/dashboard/askAica/actions';
import { useAskAicaSurface, ASK_AICA_SURFACE } from '../../context/AskAicaSurfaceContext';
import { Mixpanel } from '../../../../../../../../utils/mixpanel';
import { AICA_ASK_AICA_FEEDBACK_SENT } from '../../../../../../../../utils/constants/mixpanelEvents/investorEvents';

import MagicUploadIcon from '../../../../../../../../assets/Svg/MagicuploadIcon';
import AskAicaGreeting from '../AskAicaGreeting/AskAicaGreeting';
import DataSourceSelector from '../DataSourceSelector/DataSourceSelector';
import CompanyChip from '../CompanyChip/CompanyChip';
import CompanyPicker from '../CompanyPicker/CompanyPicker';
import MentionDropdown from './MentionDropdown';
import { useFeedbackMention, stripMention } from './useFeedbackMention';

import StopChatIcon from '../../../../../../../../assets/Svg/StopChatIcon';

import styles from './AskAicaInputBox.module.scss';

// Re-exported for tests that unit-test the pure detector against the component.
export { containsCompleteMention } from './useFeedbackMention';

interface AskAicaInputBoxProps {
    onSendMessage: (message: string, sources?: string[], flowKey?: string, agentInput?: AgentInput) => (() => void) | undefined;
    isLoading: boolean;
    // Mobile full-screen surface (RECUR-27483): the screen supplies its own
    // headline and pins the input to the bottom, so skip the desktop empty-state
    // greeting + centering and apply the mobile input styling instead.
    mobileLayout?: boolean;
    // Mobile-only "✦ Suggestions" pill inside the shell — toggles the screen's
    // suggestion strip (rendered by the parent above the input).
    suggestionsToggle?: { open: boolean; onToggle: () => void };
    // Shared abort holder. Sends that bypass this input (suggestion pills,
    // question badges) return an abort fn the internal ref never sees — the
    // parent stores those here so the stop button can cancel ANY stream.
    abortRef?: React.MutableRefObject<(() => void) | undefined>;
}

const AskAicaInputBox: React.FC<AskAicaInputBoxProps> = ({
    onSendMessage,
    isLoading = false,
    mobileLayout = false,
    suggestionsToggle,
    abortRef,
}) => {
    const internalAbortRef = useRef<(() => void) | undefined>(undefined);
    const abortFunctionRef = abortRef ?? internalAbortRef;

    const {
        messages,
        isLoadingChatHistory,
        selectedSources,
        setSelectedSources,
        surface,
        enableFeedbackMention,
        askBack,
        currentCompany,
        replyToGate,
        dismissAskBack,
        clearCompany,
    } = useAskAicaSurface();

    const [message, setMessage] = useState('');

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const backdropRef = useRef<HTMLDivElement>(null);

    const {
        feedbackMode: rawFeedbackMode,
        showMentionDropdown: rawShowMentionDropdown,
        syncDropdown,
        autoSpaceMention,
        handleMentionSpaceKey,
        completeMentionInline,
        handleMentionKey,
        buildBackdropHtml,
        feedbackHasContent,
    } = useFeedbackMention(message, setMessage, textareaRef, styles.MentionHighlight);

    // @chuckNorris feedback-mention is leads-only — always inert on the
    // portfolio surface, regardless of what the text happens to contain.
    const feedbackMode = enableFeedbackMention && rawFeedbackMode;
    const showMentionDropdown = enableFeedbackMention && rawShowMentionDropdown;
    // Auto-resize textarea. For an empty field the correct height is a single row
    // (rows={1} + min-height); deriving it from scrollHeight here can over-report
    // during the modal's open animation on first paint, rendering the composer tall
    // until the first keystroke re-measures. Only pin scrollHeight when there IS
    // content — the multi-line growth path is unchanged; empty = one line on both
    // the leads and portfolio surfaces (visually identical to before for leads,
    // which already rendered one line for an empty field).
    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        if (message) {
            el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
        }
    }, [message]);

    // Auto-focus textarea when component mounts
    useEffect(() => {
        const focusTimeout = setTimeout(() => {
            textareaRef.current?.focus();
        }, 350);
        return () => clearTimeout(focusTimeout);
    }, []);

    const stopChat = () => {
        try {
            abortFunctionRef.current?.();
        } catch (error) {
            console.error('Error stopping chat:', error);
        } finally {
            abortFunctionRef.current = undefined;
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const caretPos = e.target.selectionStart ?? e.target.value.length;
        // A keystroke that just completed a hand-typed @chuckNorris gets its
        // separating space here, so the popover and the stored value agree on it.
        const { value, caret } = autoSpaceMention(e.target.value, caretPos);
        setMessage(value);
        // Pass the caret so a partial typed mid-string (not just at the end)
        // still opens the popover.
        syncDropdown(value, caret);
    };

    const handleScroll = () => {
        if (backdropRef.current && textareaRef.current) {
            backdropRef.current.scrollTop = textareaRef.current.scrollTop;
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (isLoading) {
            stopChat();
            return;
        }

        if (feedbackMode) {
            // Strip the @chuckNorris trigger before sending — it is a routing
            // affordance, not feedback content. A-15: block if nothing remains.
            const textWithoutMention = stripMention(message);
            if (!textWithoutMention) return;
            Mixpanel.track(AICA_ASK_AICA_FEEDBACK_SENT, {
                messageLength: textWithoutMention.length,
                sourcesCount: selectedSources?.length,
            });
            // Feedback is an independent message — no answer link.
            abortFunctionRef.current = onSendMessage(textWithoutMention, undefined, FLOW_KEY_FEEDBACK);
            setMessage('');
            return;
        }

        if (message.trim()) {
            abortFunctionRef.current = onSendMessage(message.trim());
            setMessage('');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Let the mention popover consume its own keys (Enter/Tab/Esc) first.
        if (handleMentionKey(e)) return;
        // Then drop a space that would only duplicate the mention's auto space.
        if (handleMentionSpaceKey(e)) return;

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            // Enter must respect the same gate as the send button (e.g. a source
            // must be selected for a normal turn) — do not let it bypass canSend.
            if (canSend) handleSubmit(e);
        }
    };

    const handleDataSourceChange = (sources: string[]) => {
        setSelectedSources(sources);
    };

    // In feedback mode: require non-empty text beyond the mention itself (A-15).
    const canSend = feedbackMode ? feedbackHasContent : message.trim().length > 0 && selectedSources.length > 0;

    if (messages.length === 0 && isLoadingChatHistory) return <></>;

    return (
        <>
            {/* Sibling of the card, not a child: .InputArea is positioned, so it paints
                over this rect's lower half — the grey reads as sitting behind it. As a
                child it could only paint on top, and z-index:-1 sinks it behind the
                whole panel (.FloatingAskBar is a stacking context). */}
            {/* Both the picker and the chip are the grey rect BEHIND the composer card
                (Figma 32804:43164 / 32555:25342) — siblings, not children, so .InputArea
                paints over their lower half. See CompanyChip.module.scss. */}
            {surface === ASK_AICA_SURFACE.PORTFOLIO && askBack?.kind === 'pick' && (
                <CompanyPicker
                    candidates={askBack.candidates}
                    onSelect={(candidate) => replyToGate?.(candidate)}
                    onDismiss={dismissAskBack}
                    disabled={isLoading}
                />
            )}
            {surface === ASK_AICA_SURFACE.PORTFOLIO && currentCompany && (
                <CompanyChip companyName={currentCompany.name || GLOBAL_ASK_AICA_COPY.UNKNOWN_COMPANY} onClear={() => clearCompany?.()} />
            )}
            <div
                className={clsx(styles.InputArea, {
                    [styles.InputCentered]: !mobileLayout && messages.length === 0 && !isLoadingChatHistory,
                    [styles.MobileLayout]: mobileLayout,
                })}
            >
                {!mobileLayout && messages.length === 0 && !isLoadingChatHistory && (
                    <div className={styles.EmptyText}>
                        <AskAicaGreeting />
                    </div>
                )}

                {/* Portfolio-only docked region (net-new — RECUR-27369): the ask_back
                    picker and the resolved-company chip sit directly above the
                    composer, never as a chat bubble. Refinement (no free-text row
                    on the picker itself, RECUR-27369 redesign) happens by typing a
                    normal message in the composer below — the gate's /search
                    resolves it via the regular onSendMessage path further down. */}
                <form onSubmit={handleSubmit} className={styles.InputForm}>
                    <div className={styles.InputWrapper}>
                        {showMentionDropdown && <MentionDropdown onConfirm={completeMentionInline} />}
                        {/* TextareaContainer holds the backdrop overlay and the textarea stacked */}
                        <div className={styles.TextareaContainer}>
                            <div
                                ref={backdropRef}
                                className={styles.MentionBackdrop}
                                aria-hidden="true"
                                dangerouslySetInnerHTML={{ __html: buildBackdropHtml(message) }}
                            />
                            <textarea
                                ref={textareaRef}
                                value={message}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                onScroll={handleScroll}
                                placeholder="Ask anything"
                                className={styles.MessageInput}
                                disabled={isLoading}
                                rows={1}
                                data-testid="ask-aica-textarea"
                            />
                        </div>
                        <button
                            type="submit"
                            className={`${styles.SendButton} ${isLoading || canSend ? styles.Active : ''}`}
                            disabled={!isLoading && !canSend}
                            data-testid="ask-aica-send-button"
                        >
                            {isLoading ? <StopChatIcon /> : <span className={styles.SendIcon}>→</span>}
                        </button>
                    </div>
                </form>

                {mobileLayout && suggestionsToggle && (
                    <div className={styles.MobileActionsRow}>
                        <button
                            type="button"
                            className={clsx(styles.SuggestionsToggle, {
                                [styles.SuggestionsToggleActive]: suggestionsToggle.open,
                            })}
                            onClick={suggestionsToggle.onToggle}
                            aria-pressed={suggestionsToggle.open}
                            data-testid="suggestions-toggle-pill"
                        >
                            <MagicUploadIcon
                                colour={suggestionsToggle.open ? 'var(--primary-purple)' : 'var(--table-heading-text-color)'}
                                height="14"
                                width="14"
                            />
                            Suggestions
                        </button>
                    </div>
                )}

                {/* Mobile keeps the default sources; the picker is desktop-only (design). */}
                {!mobileLayout && (
                    <div className={styles.DataSourceRow}>
                        <DataSourceSelector
                            selectedSources={selectedSources}
                            onSelectionChange={handleDataSourceChange}
                            onClose={() => {}}
                            openAbove={true}
                            buttonStyle="input"
                        />
                    </div>
                )}
            </div>
            <div className={styles.WarningText}>AICA can make mistakes. Check important info</div>
        </>
    );
};

export default AskAicaInputBox;
